import React, { useState, useCallback, useEffect, useMemo } from "react";
import qs from "query-string";
import type { SortButtonChoice, AppliedFilterInterface } from "@shopify/polaris";
import { Select, TextField, useSetIndexFiltersMode, IndexFiltersMode } from "@shopify/polaris";
import TableFilters, { type ColumnSelection } from "../../common/TableFilters";
import { isEmpty, getCurrencySymbol, urlParamToDate } from "../../../helper_functions/utils";
import {
  allLocations,
  allSuppliers,
  allVendors,
  getNumberParamValue,
  useHorseLocationsOptions,
  useHorseVariant,
  useHorseVariantVendorsOptions,
  useSuppliersSearchOptions,
  useUser,
} from "../../../api_utils/requests";
import CustomRangePicker from "../../common/CustomDatePicker/CustomRangePicker";
import { formatCalendarDate } from "../../common/CustomDatePicker/utils";
import MoreLessFilter from "../../common/Filters/MoreLessFilter";
import type { DateRangeFilter, DecimalRangeFilter, Option, QueryParams } from "../../../api_utils/types";
import { dateLabel, horseVariantFilterLabel, moreThanLessThanLabel } from "../../common/Filters/label_tools";

const keys = {
  vendor: "horse-variants_vendor",
  location: "purchase_orders_location",
  purchaseCost: "purchase_orders_cost",
  receivedDate: "purchase_orders_received-date",
  supplierId: "purchase_orders_supplierId",
  horseVariantId: "purchase_orders_horseVariantId",
  ordered: "purchase_orders_ordered",
  received: "purchase_orders_received",
  paymentStatus: "purchase_orders_payment-status",
  paymentDueDateStatus: "purchase_orders_payment-due-date-status",
  paymentDueDate: "purchase_orders_payment-due-date",
};

type LabelDisambiguation =
  | {
      key: "location";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "purchaseCost";
      value: DecimalRangeFilter;
      label: string;
      prefix: string;
      suffix?: never;
    }
  | {
      key: "receivedDate";
      value: DateRangeFilter;
      label: string;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "vendor";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "supplierId";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "ordered";
      value: DecimalRangeFilter;
      label: string;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "received";
      value: DecimalRangeFilter;
      label: string;
      prefix?: never;
      suffix?: never;
    };

const disambiguateLabel = function ({ key, value, label, prefix, suffix }: LabelDisambiguation): string {
  switch (key) {
    case "vendor":
      return `Vendor is ${value}`;
    case "supplierId":
      return `Supplier is ${value}`;
    case "receivedDate":
      return dateLabel(value, label);
    case "location":
      return `Location is ${value}`;
    case "purchaseCost":
      return moreThanLessThanLabel(value, label, prefix, suffix);
    case "ordered":
      return moreThanLessThanLabel(value, label, prefix, suffix);
    case "received":
      return moreThanLessThanLabel(value, label, prefix, suffix);
    default:
      throw new Error(`Unknown key: ${key as any}`);
  }
};

const sortOptions = [
  { label: "Date", directionLabel: "Oldest/Newest", value: "created_at asc" },
  { label: "Date", directionLabel: "Newest/Oldest", value: "created_at desc" },
  { label: "Label", directionLabel: "A-Z", value: "label asc" },
  { label: "Label", directionLabel: "Z-A", value: "label desc" },
  { label: "Status", directionLabel: "A-Z", value: "state asc" },
  { label: "Status", directionLabel: "Z-A", value: "state desc" },
  { label: "Arrival date", directionLabel: "Latest/Earliest", value: "arrival_date asc" },
  { label: "Arrival date", directionLabel: "Earliest/Latest", value: "arrival_date desc" },
  { label: "Cost", directionLabel: "Small/Big", value: "cost asc" },
  { label: "Cost", directionLabel: "Big/Small", value: "cost desc" },
] as SortButtonChoice[];

const tabs = [
  {
    id: "purchase-orders-all",
    content: "All",
    accessibilityLabel: "All purchase orders result",
    panelID: "all-purchase-orders-tab-content",
    backendState: "",
  },
  {
    id: "purchase-orders-draft",
    content: "Draft",
    accessibilityLabel: "Draft purchase orders result",
    panelID: "draft-purchase-orders-tab-content",
    backendState: "draft",
  },
  {
    id: "purchase-orders-ordered",
    content: "Ordered",
    accessibilityLabel: "Ordered purchase orders result",
    panelID: "ordered-purchase-orders-tab-content",
    backendState: "ordered",
  },
  {
    id: "purchase-orders-closed",
    content: "Closed",
    accessibilityLabel: "Closed purchase orders result",
    panelID: "closed-purchase-orders-tab-content",
    backendState: "closed",
  },
];

export function PurchaseOrdersSearchFieldAndMoreFilters({
  setFilters,
  onSelect,
  selected,
  columnSelection,
}: {
  readonly setFilters: (params: QueryParams) => void;
  readonly onSelect: (id: number) => void;
  readonly selected: number;
  readonly columnSelection?: ColumnSelection;
}): React.JSX.Element {
  const parsedUrlSearch = qs.parse(window.location.search, { arrayFormat: "bracket" });
  const curDateMonth = useMemo(() => new Date(), []);
  curDateMonth.setUTCHours(0, 0, 0, 0);

  const initSortedCol = (parsedUrlSearch["by_sort[column]"] as string) || "created_at";
  const initSortedColDir = (parsedUrlSearch["by_sort[direction]"] as string) || "desc";
  const [sort, setSort] = useState([`${initSortedCol} ${initSortedColDir}`]);

  const init: {
    location?: string;
    purchaseCost: DecimalRangeFilter;
    receivedDate: { start?: Date; end?: Date };
    search?: string;
    vendor?: string;
    ordered: DecimalRangeFilter;
    received: DecimalRangeFilter;
  } = {
    // initially selected values (coming from URL)
    location: parsedUrlSearch.horse_location as string,
    purchaseCost: {
      more_than: parsedUrlSearch["by_cost[more_than]"] as string,
      less_than: parsedUrlSearch["by_cost[less_than]"] as string,
    },
    receivedDate: {
      start: urlParamToDate(parsedUrlSearch["by_received_date[start]"]),
      end: urlParamToDate(parsedUrlSearch["by_received_date[end]"]),
    },
    search: parsedUrlSearch.search as string,
    vendor: parsedUrlSearch.vendor as string,
    ordered: {
      more_than: parsedUrlSearch["by_ordered[more_than]"] as string,
      less_than: parsedUrlSearch["by_ordered[less_than]"] as string,
    },
    received: {
      more_than: parsedUrlSearch["by_received[more_than]"] as string,
      less_than: parsedUrlSearch["by_received[less_than]"] as string,
    },
  };
  const [location, setLocation] = useState(init.location);
  const [purchaseCost, setPurchaseCost] = useState<DecimalRangeFilter>(init.purchaseCost);
  const [ordered, setOrdered] = useState<DecimalRangeFilter>(init.ordered);
  const [received, setReceived] = useState<DecimalRangeFilter>(init.received);
  const [search, setSearch] = useState(init.search || "");
  const [selectedReceivedRange, setSelectedReceivedRange] = useState<DateRangeFilter>(init.receivedDate);
  const receivedDateIsSelected = !!selectedReceivedRange.start && !!selectedReceivedRange.end;
  const [vendor, setVendor] = useState<string>(init.vendor);
  const [paymentStatus, setPaymentStatus] = useState((parsedUrlSearch.by_payment_status as string) || "");
  const [paymentDueDateStatus, setPaymentDueDateStatus] = useState(
    (parsedUrlSearch.by_payment_due_date_status as string) || "",
  );
  const paymentDueDateStart = parsedUrlSearch["by_payment_due_date[start]"] as string;
  const paymentDueDateEnd = parsedUrlSearch["by_payment_due_date[end]"] as string;
  const [paymentDueDateRange, setPaymentDueDateRange] = useState<DateRangeFilter>({
    start: paymentDueDateStart ? new Date(`${paymentDueDateStart}T00:00:00`) : undefined,
    end: paymentDueDateEnd ? new Date(`${paymentDueDateEnd}T00:00:00`) : undefined,
  });
  const [supplierId, setSupplierId] = useState<string>(parsedUrlSearch.supplier_id as string);
  const [horseVariantId, setHorseVariantId] = useState<number | undefined>(
    getNumberParamValue(parsedUrlSearch.horse_variant_id as string),
  );

  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  useEffect(() => {
    const [column, direction] = sort[0].split(" ");

    const queryParams = {
      horse_location_id: location,
      supplier_id: supplierId,
      horse_variant_id: horseVariantId,
      by_cost: purchaseCost,
      search: search,
      by_received_date: selectedReceivedRange,
      vendor,
      by_sort: { column, direction },
      by_ordered: ordered,
      by_received: received,
      by_payment_status: paymentStatus,
      by_payment_due_date_status: paymentDueDateStatus,
      by_payment_due_date: {
        start: paymentDueDateRange.start ? formatCalendarDate(paymentDueDateRange.start) : undefined,
        end: paymentDueDateRange.end ? formatCalendarDate(paymentDueDateRange.end) : undefined,
      },
    };
    setFilters(queryParams);
  }, [
    location,
    purchaseCost,
    search,
    selectedReceivedRange,
    vendor,
    supplierId,
    horseVariantId,
    sort,
    setFilters,
    ordered,
    received,
    receivedDateIsSelected,
    paymentStatus,
    paymentDueDateStatus,
    paymentDueDateRange,
  ]);

  const { data: locations } = useHorseLocationsOptions();
  const { data: vendors } = useHorseVariantVendorsOptions();
  const { data: suppliers } = useSuppliersSearchOptions();
  const { data: horseVariantData } = useHorseVariant(horseVariantId);
  const horseVariantLabel = horseVariantFilterLabel(
    horseVariantData?.horseVariant.title,
    horseVariantData?.horseVariant.variant_title,
  ) || horseVariantId?.toString();

  const handleVendorChange = useCallback((value: string) => {
    setVendor(value === allVendors ? undefined : value);
  }, []);
  const handleSupplierIdChange = useCallback((value: string) => {
    setSupplierId(value === allSuppliers ? undefined : value);
  }, []);

  const handleLocationChange = useCallback((value: string) => {
    setLocation(value === allLocations ? undefined : value);
  }, []);

  const handleReceivedMonthChange = setSelectedReceivedRange;

  const handleVendorRemove = useCallback(() => {
    setVendor(undefined);
  }, []);
  const handleSupplierIdRemove = useCallback(() => {
    setSupplierId(undefined);
  }, []);
  const handleHorseVariantIdRemove = useCallback(() => {
    setHorseVariantId(undefined);
  }, []);
  const handleLocationRemove = useCallback(() => {
    setLocation(undefined);
  }, []);
  const handlePurchaseCostRemove = useCallback((): void => {
    setPurchaseCost({ more_than: undefined, less_than: undefined });
  }, []);
  const handleOrderedRemove = useCallback(() => {
    setOrdered({ more_than: undefined, less_than: undefined });
  }, []);
  const handleReceivedRemove = useCallback(() => {
    setReceived({ more_than: undefined, less_than: undefined });
  }, []);

  const handleReceivedDateRemove = useCallback(() => {
    setSelectedReceivedRange({
      start: undefined,
      end: undefined,
    });
  }, []);

  const handleSearchRemove = useCallback(() => {
    setSearch("");
  }, []);

  const handlePaymentStatusRemove = useCallback((): void => {
    setPaymentStatus("");
  }, []);
  const handlePaymentDueDateStatusRemove = useCallback((): void => {
    setPaymentDueDateStatus("");
  }, []);
  const handlePaymentDueDateRemove = useCallback((): void => {
    setPaymentDueDateRange({ start: undefined, end: undefined });
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    handleSearchRemove();
    handleHorseVariantIdRemove();
    handleLocationRemove();
    handleReceivedDateRemove();
    handleVendorRemove();
    handlePurchaseCostRemove();
    handleOrderedRemove();
    handleReceivedRemove();
    handlePaymentStatusRemove();
    handlePaymentDueDateStatusRemove();
    handlePaymentDueDateRemove();
  }, [
    handleSearchRemove,
    handleHorseVariantIdRemove,
    handleLocationRemove,
    handleReceivedDateRemove,
    handleVendorRemove,
    handlePurchaseCostRemove,
    handleOrderedRemove,
    handleReceivedRemove,
    handlePaymentStatusRemove,
    handlePaymentDueDateStatusRemove,
    handlePaymentDueDateRemove,
  ]);

  const filters = [
    {
      key: keys.paymentStatus,
      label: "Payment status",
      filter: (
        <Select
          label="Payment status"
          onChange={setPaymentStatus}
          options={[{ label: "Any", value: "" }, "Unpaid", "Partially paid", "Paid"]}
          value={paymentStatus}
        />
      ),
    },
    {
      key: keys.paymentDueDateStatus,
      label: "Payment due date status",
      filter: (
        <Select
          label="Payment due date status"
          onChange={setPaymentDueDateStatus}
          options={[{ label: "Any", value: "" }, "Overdue", "Not overdue"]}
          value={paymentDueDateStatus}
        />
      ),
    },
    {
      key: keys.paymentDueDate,
      label: "Payment due date",
      filter: <CustomRangePicker dateRange={paymentDueDateRange} setDateRange={setPaymentDueDateRange} />,
    },
    {
      key: keys.horseVariantId,
      label: "Variant",
      filter: <TextField autoComplete="on" disabled label="" labelHidden value={horseVariantId?.toString()} />,
      hidden: true,
    },
    {
      key: keys.vendor,
      label: "Vendor",
      filter: (
        <Select
          id="purchase-orders_vendor_select"
          label="Vendor"
          onChange={handleVendorChange}
          options={vendors satisfies Option[]}
          value={vendor || allVendors}
        />
      ),
    },
    {
      key: keys.supplierId,
      label: "Supplier",
      filter: (
        <Select
          id="purchase-orders_supplier_select"
          label="Supplier"
          onChange={handleSupplierIdChange}
          options={suppliers satisfies Option[]}
          value={supplierId || allSuppliers}
        />
      ),
    },
    {
      key: keys.location,
      label: "Destination",
      filter: (
        <Select
          id="inventory-level-histories_product-type_select"
          label="Vendor"
          onChange={handleLocationChange}
          options={locations satisfies Option[]}
          value={location || allLocations}
        />
      ),
    },
    {
      key: keys.receivedDate,
      label: "Arrival date",
      filter: <CustomRangePicker dateRange={selectedReceivedRange} setDateRange={handleReceivedMonthChange} />,
    },
    {
      key: keys.purchaseCost,
      label: "Cost",
      filter: (
        <MoreLessFilter
          align="right"
          less_than={purchaseCost.less_than}
          more_than={purchaseCost.more_than}
          prefix={getCurrencySymbol(userCurrency)}
          setStateCallback={setPurchaseCost}
          targetState={purchaseCost}
          type="currency"
        />
      ),
    },
    {
      key: keys.ordered,
      label: "Ordered",
      filter: (
        <MoreLessFilter
          less_than={ordered.less_than}
          more_than={ordered.more_than}
          setStateCallback={setOrdered}
          targetState={ordered}
        />
      ),
    },
    {
      key: keys.received,
      label: "Received",
      filter: (
        <MoreLessFilter
          less_than={received.less_than}
          more_than={received.more_than}
          setStateCallback={setReceived}
          targetState={received}
          type="integer"
        />
      ),
    },
  ];

  const appliedFilters: AppliedFilterInterface[] = [];
  if (paymentStatus) {
    appliedFilters.push({
      key: keys.paymentStatus,
      label: `Payment status: ${paymentStatus}`,
      onRemove: handlePaymentStatusRemove,
    });
  }
  if (paymentDueDateStatus) {
    appliedFilters.push({
      key: keys.paymentDueDateStatus,
      label: `Payment due date status: ${paymentDueDateStatus}`,
      onRemove: handlePaymentDueDateStatusRemove,
    });
  }
  if (paymentDueDateRange.start || paymentDueDateRange.end) {
    appliedFilters.push({
      key: keys.paymentDueDate,
      label: dateLabel(paymentDueDateRange, "Payment due date"),
      onRemove: handlePaymentDueDateRemove,
    });
  }
  if (!isEmpty(horseVariantId)) {
    appliedFilters.push({
      key: keys.horseVariantId,
      label: `Variant: ${horseVariantLabel}`,
      onRemove: handleHorseVariantIdRemove,
    });
  }
  if (!isEmpty(vendor)) {
    const key = keys.vendor;
    appliedFilters.push({
      key,
      label: disambiguateLabel({ key: "vendor", value: vendor }),
      onRemove: handleVendorRemove,
    });
  }
  if (!isEmpty(supplierId)) {
    const key = keys.supplierId;
    const supplierLabel: string | undefined =
      locations.length > 0 && suppliers.find((element) => element.value === supplierId).label;
    appliedFilters.push({
      key,
      label: disambiguateLabel({ key: "supplierId", value: supplierLabel }),
      onRemove: handleSupplierIdRemove,
    });
  }
  if (!isEmpty(location)) {
    const key = keys.location;
    const value: string | undefined =
      locations.length > 0 && locations.find((element) => element.value === location).label;
    appliedFilters.push({
      key,
      label: disambiguateLabel({ key: "location", value }),
      onRemove: handleLocationRemove,
    });
  }
  if (!isEmpty(purchaseCost.more_than) || !isEmpty(purchaseCost.less_than)) {
    const key = keys.purchaseCost;
    appliedFilters.push({
      key,
      label: disambiguateLabel({
        key: "purchaseCost",
        value: purchaseCost,
        label: "Cost",
        prefix: getCurrencySymbol(userCurrency),
      }),
      onRemove: handlePurchaseCostRemove,
    });
  }
  if (receivedDateIsSelected) {
    const key = keys.receivedDate;
    appliedFilters.push({
      key,
      label: disambiguateLabel({ key: "receivedDate", value: selectedReceivedRange, label: "Arrival date" }),
      onRemove: handleReceivedDateRemove,
    });
  }
  if (!isEmpty(ordered.more_than) || !isEmpty(ordered.less_than)) {
    const key = keys.ordered;
    appliedFilters.push({
      key,
      label: disambiguateLabel({ key: "ordered", value: ordered, label: "Ordered" }),
      onRemove: handleOrderedRemove,
    });
  }
  if (!isEmpty(received.more_than) || !isEmpty(received.less_than)) {
    const key = keys.received;
    appliedFilters.push({
      key,
      label: disambiguateLabel({ key: "received", value: received, label: "Received" }),
      onRemove: handleReceivedRemove,
    });
  }

  const hasPaymentFilters = paymentStatus || paymentDueDateStatus || paymentDueDateRange.start || paymentDueDateRange.end;
  const { mode, setMode } = useSetIndexFiltersMode(
    parsedUrlSearch.search || parsedUrlSearch.horse_variant_id || hasPaymentFilters ? IndexFiltersMode.Filtering : undefined,
  );

  return (
    <TableFilters
      appliedFilters={appliedFilters}
      canCreateNewView={false}
      cancelAction={{
        onAction: () => {},
        disabled: false,
        loading: false,
      }}
      columnSelection={columnSelection}
      filters={filters}
      mode={mode}
      onClearAll={handleFiltersClearAll}
      onQueryChange={setSearch}
      onQueryClear={handleSearchRemove}
      onSelect={onSelect}
      onSort={setSort}
      queryPlaceholder="Search purchase orders"
      queryValue={search}
      selected={selected}
      setMode={setMode}
      sortOptions={sortOptions}
      sortSelected={sort}
      tabs={tabs}
    />
  );
}
